import { Directive, Component, forwardRef } from '@angular/core';
import { AfterContentInit, OnInit, OnDestroy } from '@angular/core';
import { Input, HostBinding } from '@angular/core';
import { ContentChildren, QueryList } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { configService, Configuration } from '@unpispas/upp-base';

/**
 * Enumeration for grid configuration keys.
 * Defines the keys used in the configuration object for the UppGrid component.
 */
export enum UppGridConfiguration {
    /**
     * Represents the key for the grid's view mode.
     * This determines whether the grid is displayed in 'GRID' or 'LIST' view mode.
     * - **Default**: `'GRID'`
     */
    viewmode = 'viewmode',

    /**
     * Represents the key for the grid's thumbnail size.
     * This determines the scaling factor for the grid items, where the value is 
     * typically a number representing a percentage or factor of the base size.
     * - **Default**: `10` (100% scale)
     */    
    thumbsize = 'thumbsize'
}

/**
 * A component to display when the grid is empty.
 * The content is projected from the parent component.
 */
@Component({
    selector: 'upp-grid-empty',
    template: `<ng-content></ng-content>`,
    styleUrls: []
})
export class UppGridEmptyComponent {
    // nothing to do
}

/**
 * Directive for components that handle view mode changes.
 */
@Directive({
    selector: '[uppViewMode]'    
})
export class ViewModeDirective {
    protected _viewmode: 'GRID' | 'LIST' = 'GRID';

    /**
     * Gets the current view mode.
     */    
    get viewMode(){
        return this._viewmode;
    }

    /**
     * Sets the view mode and updates child items if the value has changed.
     * @param value - The new view mode ('GRID' or 'LIST').
     */   
    set viewMode(value: 'GRID' | 'LIST'){
        this._viewmode = value;
    }
}

/**
 * Represents an individual item in the grid.
 * This component supports view mode changes ('GRID' or 'LIST').
 */
@Component({
    selector: 'upp-grid-item',
    providers: [{ 
        provide: ViewModeDirective, 
        useExisting: forwardRef(() => UppGridItemComponent) 
    }],
    template: `
        <div uppViewMode class="thumbnail-item">
            <ng-content></ng-content>
        </div>
    `,
    styleUrls: [],
})
export class UppGridItemComponent extends ViewModeDirective implements AfterContentInit {
    /** identifies the list of upp-grid-item in the template */
    @ContentChildren(ViewModeDirective, { descendants: true }) items!: QueryList<ViewModeDirective>;

    /**
     * Lifecycle hook that runs after content initialization.
     * Ensures any child items are updated with the current view mode.
     */
    ngAfterContentInit() {
        this.items.changes.subscribe(() => {
            this._updateItems();
        });

        this._updateItems();
    }

    /**
     * Updates child items with the current view mode.
     * @private
     */    
    private _updateItems() {
        if (this.items?.length > 0) {
            for(const _item of this.items){
                _item.viewMode = this._viewmode;
            };
        }
    }    

    /**
     * Gets the current view mode.
     */ 
    override get viewMode(): 'GRID' | 'LIST' {
        return super.viewMode;
    }

    /**
     * Sets the view mode and updates child items if the value has changed.
     * @param value - The new view mode ('GRID' or 'LIST').
     */   
    override set viewMode(value: 'GRID' | 'LIST'){
        if (super.viewMode != value){
            super.viewMode = value;
            this._updateItems();
        }
    }
}

/**
 * Represents a grid component that dynamically handles items and configuration.
 * Supports two view modes ('GRID' and 'LIST') and scaling via a `thumsize` value.
 */
@Component({
    selector: 'upp-grid',
    templateUrl: './upp-grid.html',
    styleUrls: ['./upp-grid.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppGridComponent implements OnInit, AfterContentInit, OnDestroy {
    /**
     * The name of the grid configuration.
     * This input is required to load configuration settings.
     */    
    @Input() name: string | null = null;

    /**
     * The desired card size
     * SMALL for square cards and LARGE for rectangular cards
     */    
    @Input() size: 'SMALL' | 'LARGE' = 'SMALL';

    private _config: Configuration | null = null;
    private _onconfig_subscription: Subscription | null = null;

    private _viewmode: 'GRID' | 'LIST' = 'GRID';
    private _thumsize = 10;

    /**
     * Gets the current view mode of the grid.
     */
    get ViewMode(){
        return this._viewmode;
    }

    /**
     * Computes the scaling factor based on the `thumsize` value.
     */    
    get ScaleMode(){
        return this._thumsize / 10;
    }
        
    /**
     * Checks if the grid is empty (i.e., no child items are present).
     */    
    get IsEmpty(){
        return !this.items || (this.items.length == 0);
    }

    constructor(private config: configService, private change: ChangeDetectorRef){
        // nothing to do
    }

    /**
     * Dynamically calculates the CSS classes for the grid based on the `size` inputs.
     * 
     * @returns A string representing the classes to apply to the panel.
     */    
    @HostBinding('class') get gridClasses(): string {
        if (['SMALL', 'LARGE'].includes(this.size)){
            const sizeClass = `--${this.size.toLowerCase()}`;
            return `${sizeClass}`.trim();    
        }

        return '--small';   // default value
    }

    /**
     * Refreshes the grid configuration, updating the view mode and thumbnail size.
     * Detects and applies changes only when necessary.
     * @private
     */    
    private _refreshConfiguration(){
        if (this._config){
            const _oldviewmode = this._viewmode;     // keep previous value to check for changes
            const _newviewmode = this._config.get(UppGridConfiguration.viewmode) as 'GRID' | 'LIST' || this._viewmode;
            if (['GRID', 'LIST'].includes(_newviewmode)){
                this._viewmode = _newviewmode;
            }

            if (_oldviewmode != this._viewmode){
                this._updateItems();
            }

            const _newthumsize = Number(this._config.get(UppGridConfiguration.thumbsize)) || this._thumsize; 
            if (!isNaN(_newthumsize)) {
                this._thumsize = _newthumsize;
            }

            this.change.markForCheck();
        }
    }

    /**
     * Obtains the configuration instance for this specific grid (by name).
     * @private
     */   
    private get configuration(){
        if ((this.name) && (this._config == null)){
            this._config = this.config.getConfiguration(this.name);
        }
        return this._config;
    }

    /**
     * Lifecycle hook that initializes the grid configuration and subscribes to updates.
     */
    ngOnInit(){
        const _config = this.configuration;
        if (_config){
            // initialize the configuration values
            if (_config.get(UppGridConfiguration.viewmode) == null){
                _config.set(UppGridConfiguration.viewmode, this._viewmode);
            }

            if (_config.get(UppGridConfiguration.thumbsize) == null){
                _config.set(UppGridConfiguration.thumbsize, this._thumsize);
            }

            _config.commit();

            this._onconfig_subscription = _config.OnUpdated.subscribe(
            () => {
                this._refreshConfiguration();
            });

            this._refreshConfiguration();    
        }
    }

    /**
     * Lifecycle hook that cleans up resources when the component is destroyed.
     */
    ngOnDestroy(): void {
        if (this._onconfig_subscription){
            this._onconfig_subscription.unsubscribe();
            this._onconfig_subscription = null;
        }
    }

    @ContentChildren(ViewModeDirective, { descendants: true }) items!: QueryList<ViewModeDirective>;

    /**
     * Lifecycle hook that updates child items after content initialization.
     */    
    ngAfterContentInit() {
        this.items.changes.subscribe(() => {
            this._updateItems();
        });

        this._updateItems();
    }
    
    /**
     * Updates all child grid items with the current view mode.
     * @private
     */    
    private _updateItems() {
        if (this.items?.length > 0) {
            for(const _item of this.items){
                _item.viewMode = this._viewmode;
            };
        }   
        
        this.change.markForCheck();
    }
}
